import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Dropdown, TextInput, InputStatuses, ButtonVariants } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { REGEX } from '@/config/regex.constants';
import {
  createUnitTypeOptions,
  WORKING_DECLARATION_LABEL_KEYS,
  UNIT_TYPES,
} from '@/config/unitTypes.constants';
import { SURPLUS_WRITEOFF_TYPES } from '@/config/surplusWriteoff.constants';
import styles from './SurplusWriteoffModal.module.scss';

export const SURPLUS_WRITEOFF_MODES = Object.freeze({ add: 'add', edit: 'edit' });

// A safe stand-in before the modal has ever been opened with a row.
const EMPTY_ROW = { byUnit: {}, rawByUnit: {} };

// Matches declarationUnits.js / incomingShipmentUnits.js — every row here carries the same three.
const { buildRowUnitOptions } = createUnitTypeOptions({
  ...WORKING_DECLARATION_LABEL_KEYS,
  [UNIT_TYPES.tracking]: 'UNIT_TRACKING',
});

/**
 * Props:
 *   open          whether the modal is shown
 *   onClose       close handler (✕ / Cancel)
 *   mode          SURPLUS_WRITEOFF_MODES.add | .edit — caller derives it from
 *                 row.hasSurplusOrWriteOff
 *   row           the declaration row view model — byUnit, rawByUnit, unitType,
 *                 rawSurplus, rawWriteOff
 *   onSubmit      ({ type, quantity, unit }) => void — called once validation passes
 *   isSubmitting  disables/loads the primary button while the mutation is in flight
 */
export const SurplusWriteoffModal = ({
  open,
  onClose,
  mode,
  row,
  onSubmit,
  isSubmitting = false,
}) => {
  const t = useTranslations();

  // Retained across the close so the caller clearing its row can't empty the dialog mid-exit.
  const shown = useRef({ mode, row });
  if (open) shown.current = { mode, row };
  const activeMode = (open ? mode : shown.current.mode) ?? SURPLUS_WRITEOFF_MODES.add;
  const activeRow = (open ? row : shown.current.row) ?? EMPTY_ROW;
  const isEdit = activeMode === SURPLUS_WRITEOFF_MODES.edit;

  const [type, setType] = useState(SURPLUS_WRITEOFF_TYPES.surplus);
  const [unitType, setUnitType] = useState(UNIT_TYPES.declaration);
  const [quantity, setQuantity] = useState('');
  const [quantityError, setQuantityError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setType(
      activeRow.rawWriteOff != null
        ? SURPLUS_WRITEOFF_TYPES.writeOff
        : SURPLUS_WRITEOFF_TYPES.surplus,
    );
    // Stored surplus/write-off is always in the declaration unit today (declarationsMappers.js).
    setUnitType(isEdit ? UNIT_TYPES.declaration : (activeRow.unitType ?? UNIT_TYPES.declaration));
    setQuantity(isEdit ? String(activeRow.rawSurplus ?? activeRow.rawWriteOff ?? '') : '');
    setQuantityError(null);
  }, [open, isEdit, activeRow]);

  const typeOptions = useMemo(
    () => [
      { value: SURPLUS_WRITEOFF_TYPES.surplus, label: t('SURPLUS_WRITEOFF_TYPE_SURPLUS') },
      { value: SURPLUS_WRITEOFF_TYPES.writeOff, label: t('SURPLUS_WRITEOFF_TYPE_WRITE_OFF') },
    ],
    [t],
  );
  const unitOptions = useMemo(
    () => buildRowUnitOptions(t, activeRow.byUnit),
    [t, activeRow.byUnit],
  );

  const handleTypeChange = useCallback((event) => {
    setType(event.target.value);
    setQuantityError(null);
  }, []);
  const handleUnitChange = useCallback((event) => {
    setUnitType(event.target.value);
    setQuantityError(null);
  }, []);
  const handleQuantityChange = useCallback((event) => {
    setQuantity(event.target.value);
    setQuantityError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!REGEX.POSITIVE_DECIMAL.test(quantity)) {
      setQuantityError(t('VALIDATION_POSITIVE_NUMBER'));
      return;
    }
    const amount = Number(quantity);
    // Surplus is display-only and never reduces the pool; only a write-off is capped.
    const availableQty = activeRow.rawByUnit?.[unitType]?.availableQty ?? 0;
    // Editing an existing write-off compares against the pool before that write-off was taken.
    const existingWriteOffQty = isEdit ? (activeRow.rawByUnit?.[unitType]?.writeOffQty ?? 0) : 0;
    const maxWriteOffQty = availableQty + existingWriteOffQty;
    if (type === SURPLUS_WRITEOFF_TYPES.writeOff && amount > maxWriteOffQty) {
      setQuantityError(t('SURPLUS_WRITEOFF_QUANTITY_EXCEEDS_AVAILABLE'));
      return;
    }
    onSubmit({ type, quantity: amount, unit: activeRow.byUnit?.[unitType]?.unit });
  }, [quantity, type, unitType, activeRow, isEdit, onSubmit, t]);

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
      {
        label: isEdit ? t('COMMON_SAVE') : t('COMMON_ADD'),
        variant: ButtonVariants.primary,
        onClick: handleSubmit,
        disabled: isSubmitting,
        loading: isSubmitting,
      },
    ],
    [t, isEdit, onClose, handleSubmit, isSubmitting],
  );

  const title = t(
    isEdit
      ? 'DECLARATIONS_ACTION_EDIT_SURPLUS_WRITE_OFF'
      : 'DECLARATIONS_ACTION_ADD_SURPLUS_WRITE_OFF',
  );

  return (
    <Modal open={open} onClose={onClose} title={title} buttons={buttons}>
      <div className={styles.body}>
        <Dropdown
          label={t('FORM_SELECT_TYPE')}
          placeholder={t('FORM_SELECT_TYPE')}
          required
          value={type}
          onChange={handleTypeChange}
          options={typeOptions}
        />
        <Dropdown
          label={t('FIELD_UNIT')}
          placeholder={t('FORM_SELECT_UNIT')}
          required
          value={unitType}
          onChange={handleUnitChange}
          options={unitOptions}
        />
        <TextInput
          label={t('FIELD_QUANTITY')}
          placeholder={t('FORM_QUANTITY_PLACEHOLDER')}
          required
          type="number"
          value={quantity}
          onChange={handleQuantityChange}
          status={quantityError ? InputStatuses.error : InputStatuses.default}
          message={quantityError}
        />
      </div>
    </Modal>
  );
};
